import {Inject, Injectable} from '@angular/core';
import {CareproviderUnitItem} from '../../../types/CareproviderUnitItem';
import {SchemaCodeSnippetIdEnum} from '../../../enums/SchemaEnums';
import {EnumsService} from '../../../services/enums.service';
import {WeekDayIndexesEnum, WeekDaysEnglishEnum} from '../../../enums/CalendarEnums';
import {DOCUMENT} from '@angular/common';
import {CodeSnippetsService} from '../../../services/code-snippets.service';
import {ToolsService} from '../../../services/tools.service';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {CareService} from '../../../types/CareService';
import {BookingHelperService} from '../../../services/booking-helper.service';

@Injectable()
export class CareServiceSchemaService {
  constructor(
    private enumsService: EnumsService,
    private headerScriptService: CodeSnippetsService,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    private bookingHelper: BookingHelperService,
    @Inject(DOCUMENT) private doc: any,
  ) {}

  public addSchema(clinic: CareproviderUnitItem, careService: CareService): void {
    const schema = this.createServiceJsonSchema(clinic, careService);
    this.headerScriptService.addScriptToHead(schema);
  }

  public removeSchema(): void {
    this.headerScriptService.removeScriptsFromHead(`#${SchemaCodeSnippetIdEnum.MEDICAL_SERVICE}`);
  }

  private createServiceJsonSchema(clinic: CareproviderUnitItem, careService: CareService): any {
    const offers = {
      '@type': 'Offer',
      availability: 'http://schema.org/InStock',
    };
    if (careService.price && careService.price) {
      offers['price'] = careService.price;
      offers['priceCurrency'] = 'SEK';
    }

    const jsonSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: careService.name,
      description: this.tools.removeHtmlTags(careService.description),
      url: this.urlBuilder.CreateStaticLink(
        this.urlBuilder.CreateCareServiceRouterLink(clinic.id, clinic.urlPath, careService.id, careService.urlPath),
      ),
      offers: offers,
    };

    const provider = {
      '@type': 'MedicalClinic',
      url: clinic.caregiverContactInfo?.websiteUrl,
    };

    const address = {};
    if (clinic.address) {
      address['@type'] = 'PostalAddress';
      address['addressCountry'] = 'Sverige';
      if (clinic.address.street) {
        address['streetAddress'] = clinic.address.street;
      }
      if (clinic.address.city) {
        address['addressLocality'] = clinic.address.city;
      }
      if (clinic.address.zip) {
        address['postalCode'] = clinic.address.zip;
      }
      if (clinic.address.county) {
        address['addressRegion'] = clinic.address.county;
      }
      provider['address'] = address;
    }

    if (clinic.caregiverUnitImages) {
      const {images, thumbnailUrl, logoUrl} = clinic.caregiverUnitImages;
      if (images?.length) {
        provider['image'] = this.tools.getImageUrl(images[0].url);
      } else if (thumbnailUrl) {
        provider['image'] = this.tools.getImageUrl(thumbnailUrl);
      }
      if (logoUrl) {
        provider['logo'] = this.tools.getImageUrl(logoUrl);
      }
    }

    const openHours = [];
    if (clinic.serviceHours && clinic.serviceHours.surgeryHours) {
      clinic.serviceHours.surgeryHours.forEach((hours) => {
        for (let i = hours.fromDay; i <= hours.toDay; i++) {
          const dayKey = this.enumsService.getEnumKeyByEnumValue(WeekDayIndexesEnum, i);
          openHours.push({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: `https://schema.org/${WeekDaysEnglishEnum[dayKey]}`,
            opens: hours.fromTime,
            closes: hours.toTime,
          });
        }
      });
      provider['openingHoursSpecification'] = openHours;
    }

    const employee = clinic.staffMembers?.map((employee) => {
      return {
        '@type': 'Person',
        name: employee.name,
        jobTitle: this.tools.removeHtmlTags(employee.description),
      };
    });
    if (employee) {
      provider['employee'] = employee;
    }

    const aggregateRating = {};
    if (clinic.reviewSummary && clinic.reviewSummary.rating > 0 && clinic.reviewSummary.reviews > 0) {
      aggregateRating['@type'] = 'AggregateRating';
      aggregateRating['ratingValue'] = clinic.reviewSummary.rating;
      aggregateRating['reviewCount'] = clinic.reviewSummary.reviews;
      aggregateRating['itemReviewed'] = {
        '@type': 'MedicalClinic',
        name: clinic.name,
      };
      provider['aggregateRating'] = aggregateRating;
    }

    const review = [];
    const filteredReviews =
      clinic.reviews && clinic.reviews.length ? clinic.reviews.filter((item) => item.average > 0) : null;
    if (filteredReviews) {
      filteredReviews.forEach((item) => {
        review.push({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: item.author,
          },
          datePublished: item.metaDateCreatedFormatted,
          reviewBody: this.tools.removeHtmlTags(item.comment),
          reviewRating: {
            '@type': 'Rating',
            bestRating: 5,
            ratingValue: item.average,
            worstRating: 1,
          },
          itemReviewed: {
            '@type': 'MedicalClinic',
            name: clinic.name,
          },
        });
      });

      provider['review'] = review;
    }

    jsonSchema['provider'] = provider;

    const mainAggregateRating = {};
    if (clinic.reviewSummary && clinic.reviewSummary.rating > 0 && clinic.reviewSummary.reviews > 0) {
      mainAggregateRating['@type'] = 'AggregateRating';
      mainAggregateRating['ratingValue'] = clinic.reviewSummary.rating;
      mainAggregateRating['reviewCount'] = clinic.reviewSummary.reviews;
      mainAggregateRating['itemReviewed'] = {
        '@type': 'MedicalClinic',
        name: clinic.name,
      };
      jsonSchema['aggregateRating'] = mainAggregateRating;
    }

    // const potentialAction = {};
    // if (careService.bookingConfigurationData && careService.bookingConfigurationData.length > 0) {
    //   const bookingRouterLink = this.bookingHelper.getBookingRouterLink(careService.bookingConfigurationData[0]);
    //   if (bookingRouterLink.length > 0) {
    //     const bookingUrl = this.urlBuilder.CreateStaticLink(bookingRouterLink);
    //     potentialAction['@type'] = 'ReserveAction';
    //     potentialAction['target'] = {
    //       '@type': 'EntryPoint',
    //       urlTemplate: bookingUrl,
    //       inLanguage: 'Svenska',
    //       actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform'],
    //     };
    //
    //     jsonSchema['potentialAction'] = potentialAction;
    //   }
    // }

    const schemaScript = this.doc.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = SchemaCodeSnippetIdEnum.MEDICAL_SERVICE;
    schemaScript.innerHTML = JSON.stringify(jsonSchema);
    return schemaScript;
  }
}
